import React, { FC, ReactElement, ReactNode, useEffect, useState } from "react";
import {
  DetailsList,
  DetailsListLayoutMode,
  IColumn,
  Selection,
  SelectionMode,
  Spinner,
  SpinnerSize,
  TooltipHost,
} from "@fluentui/react";
import { useAppState } from "../../context/AppContext";
import { OrderDisplay, columns } from "./OrdersTableDef";
import { OrderLine } from "../../lib/models/OrderLine";

type OrdersTableProps = {
  onSelectionChange?: (selection: OrderDisplay[]) => void;
};

const getClassification = (line: OrderLine) => {
  const fields = [line.bizArea, line.productGroup, line.productType, line.orderClassification];

  return fields.filter((f) => !!f).join("-");
};

export const OrdersTable: FC<OrdersTableProps> = ({ onSelectionChange }) => {
  const { state } = useAppState();
  const [lines, setLines] = useState<Array<OrderDisplay>>([]);
  const [selectionKey, setSelectedKey] = useState<string>("");

  const onSelectionChanged = () => {
    onSelectionChange?.(selection.getSelection());
  };

  let selection = new Selection<OrderDisplay & { key?: string | number }>({
    onSelectionChanged,
    canSelectItem: (i) => {
      // Only allow selection of items that are not sent or have an order number
      return !i.sent || (i.sent && i.notAllHasOrderNumber);
    },
  }) as any;

  useEffect(() => {
    const maps = state.orderLines.reduce((acc: { [key: string]: OrderDisplay }, line) => {
      const hasError = line.businessCentralStatusCode?.toLowerCase() == "error";
      const errorMessage = line.businessCentralStatusMessage?.split("Correlation")[0] || "";
      const hasOrderNo = !!line.orderNo;

      if (acc[line.deliveryId]) {
        acc[line.deliveryId].amount += line.itemQuantity * line.itemUnitPrice;

        if (!hasOrderNo && acc[line.deliveryId].orderNumber) {
          acc[line.deliveryId].notAllHasOrderNumber = true;
        }
        if (hasOrderNo && !acc[line.deliveryId].orderNumber) {
          acc[line.deliveryId].orderNumber = line.orderNo;
        }
        if (hasError) {
          acc[line.deliveryId].hasError = true;
          acc[line.deliveryId].errors.push(errorMessage);
        }
      } else {
        acc[line.deliveryId] = {
          amount: line.itemQuantity * line.itemUnitPrice,
          description: line.orderDescription,
          orderNumber: line.orderNo || "",
          notAllHasOrderNumber: !hasOrderNo,
          postId: line.deliveryId,
          kStatus: line.contractStatus,
          orderClassification: getClassification(line),
          installationComplete: line.installtionCompleteDate
            ? new Date(line.installtionCompleteDate).toLocaleString("nb-no", {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
              })
            : "",
          sent: line.sentBusinessCentral,
          hasError,
          errors: hasError ? [errorMessage] : [],
        };
      }
      return acc;
    }, {});

    setSelectedKey(new Date().getTime().toString());
    onSelectionChange?.([]);
    setLines(Object.values(maps));
  }, [state.orderLines]);

  const renderItemColumn = (item: OrderDisplay, _?: number, column?: IColumn): ReactNode => {
    const fieldContent = item[column?.fieldName as keyof OrderDisplay] as string;
    if (!fieldContent || !column) return;
    if (column.key === "sent") {
      let coreComponent: ReactNode = null;
      let tooltip: ReactElement | string | ReactElement[] = "";
      if (item.sent && item.orderNumber && !item.hasError && !item.notAllHasOrderNumber) {
        coreComponent = <i className={`ms-Icon ms-Icon--SkypeCheck icon--ok`}></i>;
        tooltip = "Alle ordrelinjer er sendt til Business Central";
      }
      if (item.sent && item.orderNumber && !item.hasError && item.notAllHasOrderNumber) {
        coreComponent = <i className={`ms-Icon ms-Icon--SkypeCheck icon--warning`}></i>;
        tooltip = "Noen ordrelinjer mangler ordrenummer";
      }
      if (item.sent && !item.orderNumber && !item.hasError) {
        coreComponent = <Spinner size={SpinnerSize.small} />;
        tooltip = "Sender til Business Central";
      }
      if (item.sent && item.hasError) {
        coreComponent = <i className={`ms-Icon ms-Icon--Warning icon--error`}></i>;
        tooltip = item.errors.map((e) => (
          <div className="tooltip-text" key={e}>
            {e}
          </div>
        ));
      }

      if (coreComponent) {
        return (
          <TooltipHost id={`sync-tooltip-${item.postId}`} content={tooltip}>
            {coreComponent}
          </TooltipHost>
        );
      }
      return null;
    }

    if (column.key === "amount" && window?.Intl) {
      return <span>{Intl.NumberFormat("nb-NO").format(+fieldContent)}</span>;
    }

    if (column.key === "orderNumber" && fieldContent) {
      const url = state.orderLines?.find((l) => l.orderNo == fieldContent)?.orderUrl;
      if (url)
        return (
          <span>
            <a className="link" href={url}>
              {fieldContent}
            </a>
          </span>
        );
    }

    return <span>{fieldContent}</span>;
  };

  return (
    <div>
      <DetailsList
        compact={true}
        items={lines}
        setKey={selectionKey}
        columns={columns}
        layoutMode={DetailsListLayoutMode.justified}
        selectionPreservedOnEmptyClick={true}
        selection={selection}
        selectionMode={SelectionMode.multiple}
        ariaLabelForSelectionColumn="Toggle selection"
        ariaLabelForSelectAllCheckbox="Toggle selection for all items"
        onRenderItemColumn={renderItemColumn}
        checkButtonAriaLabel="select row"
      />
    </div>
  );
};
